import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

export interface CookieSettings {
  id?: string;
  enableBanner: boolean;
  consentExpiryDays: number;
  cookiePolicyUrl: string;
  privacyPolicyUrl: string;
  enableAnalytics: boolean;
  enableMarketing: boolean;
  enablePreferences: boolean;
  updatedAt?: string;
  updatedBy?: string | null;
}

export interface CookieConsentPayload {
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

interface ApiResponse<T> {
  response: T;
}

@Injectable({
  providedIn: 'root'
})
export class CookieSettingsService {
  private readonly adminUrl = `${environment.apiUrl}/admin/cookie-settings`;
  private readonly publicUrl = `${environment.apiUrl}/cookie-settings/public`;
  private readonly consentUrl = `${environment.apiUrl}/cookie/consent`;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) {}

  getAdminSettings(): Observable<CookieSettings> {
    return this.http
      .get<ApiResponse<CookieSettings>>(this.adminUrl, {
        headers: this.authService.getHeaders()
      })
      .pipe(map((response) => response.response));
  }

  updateAdminSettings(settings: CookieSettings): Observable<CookieSettings> {
    return this.http
      .put<ApiResponse<CookieSettings>>(this.adminUrl, settings, {
        headers: this.authService.getHeaders()
      })
      .pipe(map((response) => response.response));
  }

  getPublicSettings(): Observable<CookieSettings> {
    return this.http
      .get<ApiResponse<CookieSettings>>(this.publicUrl)
      .pipe(map((response) => response.response));
  }

  getMyConsent(): Observable<CookieConsentPayload | null> {
    return this.http
      .get<ApiResponse<CookieConsentPayload | null>>(`${this.consentUrl}/me`, {
        headers: this.authService.getHeaders()
      })
      .pipe(map((response) => response.response ?? null));
  }

  saveConsent(payload: CookieConsentPayload): Observable<void> {
    return this.http
      .post<ApiResponse<unknown>>(this.consentUrl, payload, {
        headers: this.authService.getHeaders()
      })
      .pipe(map(() => void 0));
  }
}
